<template>
  <section>
    <div
      v-show="!isLoaded"
      id="preloader-1"
      style="
        background-image: none;
        position: fixed;
        top: 0;
        bottom: 0;
        inset-inline-start: 0;
        inset-inline-end: 0;
      "
    >
      <div id="status">
        <div class="spinner-chase">
          <div class="chase-dot"></div>
          <div class="chase-dot"></div>
          <div class="chase-dot"></div>
          <div class="chase-dot"></div>
          <div class="chase-dot"></div>
          <div class="chase-dot"></div>
        </div>
      </div>
    </div>
    <div v-show="isLoaded">
      <b-modal
        id="touchModeAlert"
        hide-footer
        hide-header
        size="md"
        :scrollable="false"
      >
        <TouchModeAlert :disableReason="disableReason" />
      </b-modal>

      <cropperImage
        :area="cropArea"
        :type="cropType"
        :message="cropMessage"
        :errorMessage="cropErrorMessage"
        :minHeight="45"
        :minWidth="200"
        :ratio="0"
        :maxSize="2097152"
        @croppedImageEvent="onFileChange"
      />


      <b-modal
        id="showConfirmationToDisableTouchModeModal"
        hide-footer
        hide-header
        size="md"
        :scrollable="false"
      >
        <showConfirmationToDisableTouchModeModal @disableTouchMode="disableTouchMode" />
      </b-modal>

      <VpFormLayout>
        <template v-slot:content-header>
          <PageHeader
            className="d-flex align-items-center"
            :title="$t('journeyBuilder.form.journeyBuilder')"
          />
        </template>
        <template v-slot:content-body>
          <div class="journey-form wrapper">
            <p class="form-title">
              {{
                $route.name == "updateJourney"
                  ? $t("journeyBuilder.grid.updateJourney")
                  : $t("journeyBuilder.createJourney")
              }}
            </p>
            <div class="content">
              <div class="journey-logo mb-3">
                <div>
                  <button
                    type="button"
                    @click="pickImage"
                    v-if="!journey.logo"
                    class="upload-btn"
                  >
                    <b-form-file
                      accept="image/*"
                      id="imagePicker"
                      @change="onFileChange"
                      class="d-none upload-btn"
                    ></b-form-file>
                    <div class="d-flex justify-content-between">
                      <p class="mt-3 logo-label">
                        {{ $t("journeyBuilder.form.addLogoOptional") }}
                      </p>
                      <span class="mt-2 mr-2 d-flex align-items-center"
                        ><i class="mdi mdi-camera mdi-24px"></i
                      ></span>
                    </div>
                  </button>

                  <div
                    style="position: relative; inset-inline-start: 120px"
                    v-if="journey.logo"
                  >
                    <img :src="journey.logo" class="journey-img-bg" />
                    <div class="img-bg__cover"></div>
                    <b-form-file
                      accept="image/*"
                      id="imagePicker"
                      @change="onFileChange"
                      class="d-none"
                    ></b-form-file>
                    <span class="d-block">
                      <i type="button" @click="pickImage"></i>
                      <button
                        type="button"
                        @click="removeImage()"
                        class="remove-btn"
                      >
                        {{ $t("common.remove") }}
                      </button>
                    </span>
                  </div>

                  <p class="text-danger" v-if="logoError">
                    {{ $t("common.notes.logoInstructionsUploadError") }}
                  </p>
                  <div
                    class="journey-logo-note-text"
                    v-if="!logoError && !journey.logo"
                  >
                    <b>{{ $t("common.notes.note") }}: </b
                    >{{ $t("common.notes.logoInstructionsUpload") }}
                  </div>
                </div>
              </div>
              <div class="journey-name mb-3">
                <div class="input-wrapper">
                  <div class="form-group m-d-group">
                    <b-form-input
                      class="journey-input"
                      id="name"
                      name="name"
                      type="text"
                      v-model="journey.name"
                      required
                      maxlength="50"
                      :placeholder="$t('journeyBuilder.form.journeyName')"
                    />
                    <!-- <label for="name">Journey Name</label> -->
                  </div>
                </div>
                <div class="err-wrapper">
                  <div v-if="submitted && !$v.journey.name.minLength">
                    <span class="error-value">{{
                      $t("journeyBuilder.form.nameAtLeast2Char")
                    }}</span>
                  </div>
                  <div v-if="submitted && !$v.journey.name.maxLength">
                    <span class="error-value">{{
                      $t("common.validation.maximumLength50Char")
                    }}</span>
                  </div>
                </div>
              </div>
              <div>
                <b-form-textarea
                  class="journey-textarea"
                  v-model="journey.description"
                  id="description"
                  :placeholder="
                    $t('journeyBuilder.form.journeyDescriptionOptional')
                  "
                  rows="10"
                  no-resize
                ></b-form-textarea>
              </div>

              <div>
                <h4
                  class="vp-color-grey-3 vp-mt-48 vp-mb-16 vp-font-size-16 vp-font-weight-700"
                >
                  {{ $t("journeyBuilder.form.accessPermission") }}
                </h4>
                <p
                  class="vp-color-grey-3 vp-mb-32 vp-font-size-14 vp-font-weight-400"
                >
                  {{ $t("journeyBuilder.form.onlyAdminsOfSelectedSites") }}
                </p>

                <vp-treeselect
                  v-model="sites"
                  :options="sitesOptions"
                  :label="$t('common.site')"
                  :showPlaceholderIcon="true"
                  width="416"
                />

                <p
                  class="vp-color-grey-3 vp-mt-8 vp-ml-20 vp-font-size-14 vp-font-weight-400"
                  v-if="showSitesNote"
                >
                  <b>{{ $t("common.notes.note") }}:</b>
                  {{
                    $t("journeyBuilder.form.removingSitesIsNotAllowedMessage")
                  }}
                  {{ $t("journeyBuilder.form.orJourneyAssigned") }}
                </p>
              </div>

              <template v-if="userTypesEnabled">
                <div
                  class="inst-content"
                  v-if="
                    $route.name == 'updateJourney' &&
                    !touch_mode &&
                    getQRUserTypes &&
                    getQRUserTypes.length > 0
                  "
                >
                  <div class="line-sep"></div>
                  <div class="user-type">
                    <p class="title">
                      {{ $t("journeyBuilder.form.newUsersViaQuickPass") }}
                    </p>
                    <p class="content">
                      {{
                        $t(
                          "journeyBuilder.form.selectTheUserTypeForTheNewUsersMessage"
                        )
                      }}
                    </p>
                    <!-- user type  -->
                    <div class="user-types-select" id="submit-journey">
                      <vp-multiselect
                        v-model="userType"
                        :title="$t('common.userType')"
                        :options="getQRUserTypes"
                        :placeholder="$t('common.userType')"
                        :show-labels="false"
                        label="name"
                        :allowEmpty="true"
                        :disabled="disableQuickPass"
                        zIndex="2"
                      />
                      <p
                        class="vp-color-grey-3 vp-ml-20 vp-font-size-14 vp-font-weight-400"
                        v-if="disableQuickPass"
                      >
                        <b>{{ $t("common.notes.note") }}:</b>
                        {{ $t("journeyBuilder.form.dontHaveAccessUserType") }}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  class="instructions-content vp-mt-60"
                  v-if="
                    $route.name == 'updateJourney' &&
                    !touch_mode &&
                    userTypesEnabled
                  "
                >
                  <hr class="h-line" />
                  <div class="user-types">
                    <p class="text-title">
                      {{ $t("journeyBuilder.form.instructionScreen") }}
                    </p>
                    <p v-if="userTypesEnabled" class="subtitle">
                      {{ $t("journeyBuilder.form.shownUserTypes") }}:
                    </p>
                    <div
                      v-if="userTypesEnabled"
                      class="user-types-checks col-lg-12"
                    >
                      <b-form-checkbox-group v-model="selected" size="lg">
                        <b-form-checkbox
                          v-for="type in getJourneyUserTypes"
                          :key="type.id"
                          :value="type.id"
                          unchecked-value="not_accepted"
                          class="custom-checkbox-secondary custom-checkbox"
                        >
                          <p class="text">{{ type.name }}</p>
                        </b-form-checkbox>
                      </b-form-checkbox-group>
                    </div>
                  </div>
                  <div class="vaccine-input">
                    <div class="input-wrapper">
                      <div class="form-group m-d-gruop">
                        <b-form-input
                          id="vaccine"
                          name="vaccine"
                          type="text"
                          maxlength="13"
                          v-model="vaccine_column_name"
                          required
                        />
                        <label for="vaccine">{{
                          $t("journeyBuilder.form.covidColumnName")
                        }}</label>
                      </div>
                      <div class="err-wrapper">
                        <div
                          v-if="submitted && !$v.vaccine_column_name.maxLength"
                        >
                          <span class="error-value">{{
                            $t("common.validation.maximumLength13Char")
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr class="h-line" />
                </div>
              </template>

              <hr
                v-if="
                  $route.name != 'updateJourney' ||
                  ($route.name == 'updateJourney' && touch_mode)
                "
                class="h-line"
              />
              <!-- touch mode  -->
              <div class="touch-mode" @click="onUpdateTouchMode">
                <p class="label">{{ $t("journeyBuilder.form.touchMode") }}:</p>
                <b-form-checkbox
                  class="switch-gray"
                  switch
                  size="lg"
                  v-model="touch_mode"
                  :disabled="disable_touch_mode"
                  
                ></b-form-checkbox>
              </div>

              <div class="name-match" v-if="touch_mode">
                <v-radio-group v-model="name_match_mode">
                  <v-radio
                    v-for="option in nameMatchOptions"
                    :key="option.value"
                    :label="option.text"
                    :value="option.value"
                  ></v-radio>
                </v-radio-group>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:content-footer>
          <VpButton
            :text="
              $route.name == 'updateJourney'
                ? $t('common.update')
                : $t('common.next')
            "
            :disabled="!journey.name || getLoading || !sites.length"
            :isLoading="getLoading"
            :height="'37px'"
            :width="'151px'"
            @click="onJourneySubmit()"
          />

          <router-link to="/journeys">
            <VpButton
              :text="$t('common.cancel')"
              :disabled="getLoading"
              :height="'37px'"
              :width="'151px'"
              :isDanger="true"
            />
          </router-link>
        </template>
      </VpFormLayout>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import store from "@/state/store";
import PageHeader from "@/components/page-header.vue";
import { required, minLength, maxLength } from "vuelidate/lib/validators";
import TouchModeAlert from "@/components/journeyBuilder/TouchModeAlert";
import showConfirmationToDisableTouchModeModal from "./showConfirmationToDisableTouchModeModal";
import cropperImage from "@/components/sharedCells/CropperImage";
import siteService from "@/services/sites";

export default {
  name: "SubmitJourney",
  components: { 
    TouchModeAlert, 
    cropperImage, 
    PageHeader,
    showConfirmationToDisableTouchModeModal
  },
  data() {
    return {
      isLoaded: false,
      disableReason: { no_input: null, regNotReq: null },
      user_type_id: "",
      userType: {},
      journey: {
        name: null,
        description: "",
        entity_id: null,
        logo: "",
      },
      submitted: false,
      vaccine_column_name: null,
      journey_id: null,
      selected: [],
      userTypes: [],
      touch_mode: false,
      is_logo_removed: false,
      name_match_mode: "QUICK",
      disable_touch_mode: false,
      nameMatchOptions: [
        { text: this.$t("journeyBuilder.form.quickNameMatch"), value: "QUICK" },
        {
          text: this.$t("journeyBuilder.form.privateNameMatch"),
          value: "PRIVATE",
        },
      ],
      logo: {
        size: "",
        height: "",
        width: "",
        url: "",
      },
      logoError: false,
      cropMessage:
        "Image needs to be at least 200px width, 45px height and 2MB max size.",
      cropErrorMessage:
        "Couldn’t upload your logo because it needs to be at least 200px width, 45px height and 2MB max size.",
      cropArea: "box",
      cropType: "Logo",
      sitesOptions: [],
      sites: [],
      usedSites: [],
      showSitesNote: false,
      disableQuickPass: false,
      userTypesEnabled: false,
      showInstructionScreen: true,
    };
  },
  validations: {
    journey: {
      name: { minLength: minLength(2), maxLength: maxLength(50) },
      description: "",
    },
    name_match_mode: {
      required,
    },
    vaccine_column_name: { maxLength: maxLength(13) },
  },
  computed: {
    ...mapGetters("journey", [
      "getLoading",
      "getJourneyUserTypes",
      "getJourneyDetails",
      "getJourneyInputs",
      "getQRUserTypes",
      "getRegistrationNotRequiredCount",
    ]),
    ...mapGetters({
      getUserTypesOfCurrentJourney: "journeyBuilder/getJourneyDetails",
    }),
  },
  watch: {
    userType(userType) {
      this.user_type_id = userType ? userType.id : this.user_type_id;
    },
    getJourneyDetails(data) {
      const { name, description, id, settings, logo } = data;
      this.journey_id = id;
      this.journey.name = name;
      this.journey.description = description;
      this.journey.logo = logo;
      settings &&
        settings.length > 0 &&
        settings.map((type) => {
          if (type.name == "show_instructions" && type.pivot.value) {
            let newTypes = this.userTypes;
            let oldTypes = JSON.parse(type.pivot.value).filter(Boolean);
            const differantID = newTypes.filter(
              ({ userTypeId: id1 }) =>
                !oldTypes.some(({ userTypeId: id2 }) => id2 === id1)
            );
            this.userTypes = Array.from(new Set(oldTypes.concat(differantID)));
            this.userTypes.map((type) => {
              if (type.show == true) {
                this.selected.push(type.userTypeId);
              }
            });
          } else if (type.name == "vaccine_column_name") {
            this.vaccine_column_name = type.pivot.value;
          } else if (type.name == "touch_mode") {
            this.touch_mode = type.pivot.value == "1" ? true : false;
          } else if (type.name == "name_match_mode") {
            this.name_match_mode = this.touch_mode ? type.pivot.value : "QUICK";
          } else if (type.name == "quickpass_user_type") {
            if (type.pivot.value) {
              this.user_type_id = type.pivot.value;
              this.userType = this.getQRUserTypes.find(
                (item) => item.id == type.pivot.value
              );
              if (this.userType?.isDisabled) this.disableQuickPass = true;
              //if (!this.userType) this.userType = this.getQRUserTypes[0];
            } else this.userType = null;
            // else{
            //   this.userType = this.getQRUserTypes.length > 0 ? this.getQRUserTypes[0] : null;
            // }
          }
        });
      if (this.$route.name == "updateJourney" && this.touch_mode) {
        this.validateRemoteJourney({
          journey_id: this.$route.params.id,
          touch_mode: 0,
        }).then((data) => {
          if (!data.data) this.disable_touch_mode = true;
        });
      }
    },
    getJourneyUserTypes(types) {
      if (types?.length) {
        this.userTypes = types.map((type) => {
          return { userTypeId: type.id, show: false };
        });
        this.userTypesEnabled = true;
      }
    },
    selected(selected) {
      const { userTypes } = this;
      for (let prop in userTypes) {
        {
          selected.includes(userTypes[prop].userTypeId)
            ? (userTypes[prop].show = true)
            : (userTypes[prop].show = false);
        }
      }
      return userTypes;
    },
    getJourneyInputs(inputs) {
      for (const property in inputs) {
        const parentInput = inputs[property];
        for (const childProp in parentInput) {
          const childInput = parentInput[childProp];
          if (childProp == "have" && !childInput) {
            this.disable_touch_mode = true;
            this.disableReason.no_input = true;
          }
        }
      }
    },
  },
  methods: {
    ...mapActions("journey", [
      "createJourney",
      "updateJourney",
      "fetchUserTypeRelatedToJourney",
      "fetchJourneyDetails",
      "fetchJourneyInputs",
      "fetchQRUserTypes",
      "fetchRegistrationNotRequiredUserTypeCount",
      "validateRemoteJourney",
      "confirmToDisableTouchMode",
    ]),
    pickImage() {
      // document.getElementById("imagePicker").click();
      // setTimeout(() => {
      //   let uploadIcon =  document.getElementsByTagName("a").title = "Upload a new image";
      //   uploadIcon.style.display = 'none';
      // }, 110);
      this.$bvModal.show("cropImageModal");
    },
    removeImage() {
      this.journey.logo = "";
      this.fileImage = "";
      this.fileImageChanged = true;
      this.is_logo_removed = true;
    },
    onFileChange(file) {
      this.logoError = false;
      this.logo.width = "";
      this.logo.height = "";
      this.journey.logo = "";

      // if (!file || file.type.indexOf("image/") !== 0) return this.logoError = true ;
      if (file.width < 200 || file.height < 45 || file.size > 2097152)
        return (this.logoError = true);
      this.logo.size = file.size;
      this.logo.width = file.width;
      this.logo.height = file.height;
      this.logoLoaded = true;
      this.is_logo_removed = false;
      this.journey.logo = file.cropedImage;

      this.fileImageChanged = true;
      // this.fileImage = file.blobFile;
      setTimeout(() => {
        this.fileImage = file.blobFile;
      }, 1000);
    },
    onJourneySubmit() {
      this.submitted = true;
      this.$v.$touch();
      const { name, description } = this.journey;
      const {
        userTypes,
        userType,
        vaccine_column_name,
        touch_mode,
        name_match_mode,
        fileImage,
        is_logo_removed,
        fileImageChanged,
        sites,
      } = this;

      if (!this.$v.$invalid) {
        let entity = store.getters["authfack/currentEntity"];
        let formData = new FormData();
        formData.append("journey[name]", name);
        formData.append("journey[entity_id]", entity.id);
        formData.append("journey[description]", description);
        formData.append("removeLogo", is_logo_removed);
        formData.append("settings[touch_mode]", touch_mode ? 1 : 0);
        formData.append("settings[name_match_mode]", name_match_mode);
        formData.append("journey_sites", sites);
        if (this.journey.logo && fileImageChanged) {
          formData.append("settings[journey_logo]", fileImage);
        } else {
          formData.append("settings[journey_logo]", "");
        }

        if (this.$route.name == "updateJourney") {
          let stringifiedUserType = userTypes.map((type) => {
            return type;
          });
          formData.append("id", this.$route.params.id);
          formData.append("userTypes", JSON.stringify(stringifiedUserType));
          formData.append(
            "settings[quickpass_user_type]",
            userType && !touch_mode ? userType.id : ""
          );
          let vacc_col_name = vaccine_column_name
            ? vaccine_column_name
            : "Covid Vaccine";
          formData.append("settings[vaccine_column_name]", vacc_col_name);

          this.updateJourney({ formData, journey_id: this.$route.params.id, toastMsg: this.$t("common.updatedSuccessfully") });
        } else {
          this.createJourney(formData);
        }
      }
    },
    onUpdateTouchMode() {
      // this.userType = null;
      // this.user_type_id = null;
      this.selected = [];
      if (this.disable_touch_mode) {
        this.$bvModal.show("touchModeAlert");
      }else{
        if(this.touch_mode){
          this.confirmToDisableTouchMode(this.$route.params.id).then( (res)=>{
            if(res.data?.showConfirmation) this.$bvModal.show("showConfirmationToDisableTouchModeModal");
          });
        }
      }
    },
    getDifference(array1, array2) {
      return array1.filter((object1) => {
        return !array2.some((object2) => {
          return object1.id === object2.id;
        });
      });
    },
    getCroppedImage(data) {
      this.logoError = false;
      this.logo.width = "";
      this.logo.height = "";
      this.journey.logo = "";
      let file = data.croppedFile;

      if (!file || file.type.indexOf("image/") !== 0)
        return (this.logoError = true);
      this.logo.size = file.size;
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (evt) => {
        let logo = new Image();
        logo.onload = () => {
          if (
            logo.width < 200 ||
            logo.height < 45 ||
            this.logo.size > 2097152
          ) {
            return (this.logoError = true);
          }
          this.logo.width = logo.width;
          this.logo.height = logo.height;
          this.logoLoaded = true;
          this.journey.logo = URL.createObjectURL(file);
          this.is_logo_removed = false;
        };
        logo.src = evt.target.result;
        this.fileImageChanged = true;
        this.fileImage = file;
      };
      reader.onerror = (evt) => {
        console.error(evt);
      };
    },
    disableTouchMode(type) {
      if(type == 'disabled') {
        this.touch_mode = false;
      }else{
        this.touch_mode = true;
      }
      this.$bvModal.hide("showConfirmationToDisableTouchModeModal");
    }
  },
  async beforeMount() {
    if (this.$route.name == "addJourney") {
      const { data } = await siteService.getSites();
      this.sitesOptions = data;
    }

    this.$route.name == "updateJourney" &&
      (this.$store.commit("journey/setIsValidateRemoteJourney", true),
      await this.fetchQRUserTypes(this.$route.params.id),
      await this.fetchUserTypeRelatedToJourney(this.$route.params.id),
      await this.fetchJourneyInputs(this.$route.params.id),
      await this.fetchRegistrationNotRequiredUserTypeCount(
        this.$route.params.id
      ));
    const flow_id = this.getJourneyInputs
      ? Object.keys(this.getJourneyInputs)[0]
      : null;

    if (this.$route.name == "updateJourney" && flow_id) {
      if (this.getRegistrationNotRequiredCount > 1) {
        this.disable_touch_mode = true;
        this.disableReason.regNotReq = true;
      }
    }

    if (this.$route.name == "updateJourney") {
      this.sitesOptions = this.getJourneyDetails?.sites;
      this.sites = this.getJourneyDetails?.sites
        ?.filter((site) => site.isSelected)
        ?.map((site) => site.id);
      this.showSitesNote = this.getJourneyDetails?.sites?.some(
        (site) => site.isSelected && site.isDisabled
      );
    }

    this.isLoaded = true;
  },
};
</script>

<style lang="scss" scoped>
.page-title {
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: #495057;
  margin-bottom: 28px;
}
.inst-content {
  .line-sep {
    margin-top: 43px;
    margin-bottom: 39px;
    width: 442px;

    opacity: 0.08;
    border: 1px solid #000000;
  }
  .user-type {
    .title {
      font-weight: 700;
      font-size: 16px;
      line-height: 19px;
      margin-inline-start: 22px;
      color: #939393;
    }
    .content {
      font-weight: 400;
      font-size: 14px;
      line-height: 24px;
      margin-inline-start: 22px;
      letter-spacing: 0.25px;
      margin-bottom: 18px !important;
      color: #939393;
    }
  }
}

.journey-form {
  background-color: #fff;
  border-radius: 10px;
  padding: 32px;
  .form-title {
    font-weight: 900;
    font-size: 20px;
    line-height: 24px;
    color: #939393;
    opacity: 0.46;
    margin-bottom: 33px;
  }
  .name-match {
    font-weight: Bold;
    font-size: 16px;
    color: #939393;
    margin-top: 40px;
  }

  .content {
    padding: 0;
    margin-top: 0;
    width: 422px;
    .journey-name {
      margin-bottom: 17px;
    }
    .h-line {
      margin: 28px 0;
    }
    .user-types {
      margin-bottom: 36px;
      .text-title {
        font-weight: bold;
        font-size: 16px;
        line-height: 19px;
        color: #939393;
        margin-bottom: 30px;
      }
      .subtitle {
        font-weight: bold;
        font-size: 16px;
        line-height: 19px;
        margin-bottom: 33px;
        color: #c7c7c7;
      }
      .user-types-checks {
        display: flex;
        flex-wrap: wrap;
        margin-inline-end: 100px;
        .custom-checkbox {
          margin-bottom: 20px;
        }
        .text {
          margin-bottom: 0;
          font-size: 16px;
          color: #787878;
          margin-inline-start: 15px;
          margin-inline-end: 50px;
        }
      }
    }
    .touch-mode {
      display: flex;
      position: relative;
      .label {
        font-weight: Bold;
        font-size: 16px;
        color: #939393;
        margin-top: 6px;
        margin-bottom: 0;
        margin-inline-end: 52px;
      }
    }
  }
}
.err-wrapper {
  padding-inline-start: 20px;
}
.upload-btn {
  background-color: #f2f2f2;
  color: #000000;
  width: 422px;
  height: 55px;
  border-radius: 4px;
  border: 0;
}
.logo-label {
  color: #787878;
  font-weight: 700;
  font-size: 16px;
  line-height: 17px;
  letter-spacing: 0.25px;
  margin-inline-start: 20px;
}
.journey-logo-note-text {
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  letter-spacing: 0.25px;
  color: #939393;
  width: 90%;
  margin: auto;
  margin-top: 6px;
}
.journey-img-bg {
  max-width: 200px;
  height: 59px;
  border: none;
  border-radius: 4px;
  position: relative;
  inset-inline-start: -120px;
}
.remove-btn {
  position: relative;
  bottom: 40px;
  inset-inline-start: 20%;
  width: 68px;
  height: 25px;
  border: none;
  color: #fff;

  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  text-align: center;
  color: #ffffff;
  border-radius: 16px;
  background: #f46a6a;
}
.journey-input,
.journey-textarea {
  background-color: #f2f2f2;
  border: 0px;
  width: 422px;

  font-weight: 400;
  font-size: 16px;
  line-height: 17px;
  padding-inline-start: 25px;
}
.journey-textarea {
  padding-top: 20px;
}
.journey-input {
  height: 55px;
}
</style>
<style lang="scss">
#submit-journey {
  .multiselect__content-wrapper {
    overflow-x: hidden !important;
  }
}

.custom-switch
  .custom-control-input:disabled:checked
  ~ .custom-control-label::before {
  background: #f2f2f2 !important;
}
.custom-switch
  .custom-control-input:disabled:checked
  ~ .custom-control-label::after {
  opacity: 0.2 !important;
}
div#radio-group-22 {
  display: flex;
  width: 422px;
  justify-content: space-between;
}
div#submit-journey {
  width: 416px !important;
  height: 55px !important;
  background: #ffffff;
  ::placeholder {
    font-weight: 700 !important;
    font-size: 12px !important;
    line-height: 17px !important;
    letter-spacing: 0.25px;
    color: #103f5e;
    opacity: 0.8;
  }

  .mt-10 {
    margin-top: 10px !important;
  }
}
</style>

<style>
.multiselect__content {
  margin: unset !important;
}
</style>
